import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, buttonTypes, buttonSizes } from 'moods-react/Button';
import { Tooltip, tooltipCaretDirections, tooltipPosition } from 'moods-react/Tooltip';
import { IconUiDownload, IconUiEdit, IconUiBin } from 'moods-react/Icons';
import { Spinner, spinnerSizes } from 'moods-react/Spinner';
import { canUpdateProduct } from '../../services/user-permission/UserPermissionService';
import AnalyticsClient from '../../../clients/AnalyticsClient';
import productEvents from '../../../analytics/events/products';

import './TileMenu.scss';

/**
 * Sub menu that sits on top of a tile
 */

const PdfLinkGenerator = ({ pdfLink, label }) => {
  const onDownload = (e) => {
    AnalyticsClient.trackEvent(productEvents.tileAction('Download PDF'));
    window.location = pdfLink;
    e.stopPropagation();
  };
  return (
    <div className="tile-menu-component__item">
      <Button
        isDisc
        icon={IconUiDownload}
        type={buttonTypes.GHOST_LIGHT}
        size={buttonSizes.LARGE}
        onClick={onDownload}
        onTouchEnd={onDownload}
        tooltip={
          <Tooltip position={tooltipPosition.BELOW} caretDirection={tooltipCaretDirections.TOP}>
            {label}
          </Tooltip>
        }
      />
    </div>
  );
};

PdfLinkGenerator.propTypes = {
  pdfLink: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

const EditButtonGenerator = ({ editLink, label }) => {
  const onEdit = (e) => {
    AnalyticsClient.trackEvent(productEvents.tileAction('Edit'));
    window.location = editLink;
    e.stopPropagation();
  };
  return (
    <div className="tile-menu-component__item">
      <Button
        isDisc
        icon={IconUiEdit}
        type={buttonTypes.GHOST_LIGHT}
        size={buttonSizes.LARGE}
        onClick={onEdit}
        onTouchEnd={onEdit}
        tooltip={
          <Tooltip position={tooltipPosition.BELOW} caretDirection={tooltipCaretDirections.TOP}>
            {label}
          </Tooltip>
        }
      />
    </div>
  );
};

EditButtonGenerator.propTypes = {
  editLink: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

const DeleteButtonGenerator = ({ isDeleting, onDelete, label }) => {
  const onDeleteFn = (e) => {
    e.stopPropagation();
    onDelete();
  };
  return (
    <div className="tile-menu-component__item">
      {isDeleting ? (
        <Spinner size={spinnerSizes.LARGE} white />
      ) : (
        <Button
          isDisc
          icon={IconUiBin}
          type={buttonTypes.GHOST_LIGHT}
          size={buttonSizes.LARGE}
          onClick={onDeleteFn}
          onTouchEnd={onDeleteFn}
          tooltip={
            <Tooltip position={tooltipPosition.BELOW} caretDirection={tooltipCaretDirections.TOP}>
              {label}
            </Tooltip>
          }
        />
      )}
    </div>
  );
};

DeleteButtonGenerator.propTypes = {
  isDeleting: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

const mapStateToProps = (state, props) => ({ ...props, internalUser: state.config.internalUser });

const TileMenuTemplateIdComponent = (props) => {
  const shouldShow = props.templateId && props.internalUser;
  if (shouldShow) {
    return (
      <span className="tile-menu-component__template" data-templateid>
        <span className="tile-menu-component__templateLabel">ID: </span>
        {props.templateId}
      </span>
    );
  }
  return null;
};

const ConnectedTemplateIdComponent = connect(mapStateToProps)(TileMenuTemplateIdComponent);

const TileMenuComponent = (props) => {
  const canDeleteOrEdit = canUpdateProduct(props.permissions);
  const canDownload = props.pdfLink.length > 0;
  if (!canDeleteOrEdit && !canDownload) {
    return <div />;
  }
  return (
    <div className="tile-menu-component">
      {canDeleteOrEdit && <EditButtonGenerator editLink={props.editLink} label={props.labels.edit} />}
      {canDownload && <PdfLinkGenerator pdfLink={props.pdfLink} label={props.labels.download} />}
      {canDeleteOrEdit && (
        <DeleteButtonGenerator isDeleting={props.isDeleting} onDelete={props.onDelete} label={props.labels.delete} />
      )}
      <ConnectedTemplateIdComponent templateId={props.templateId} />
    </div>
  );
};

TileMenuComponent.displayName = 'TileMenu';

TileMenuComponent.propTypes = {
  permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
  labels: PropTypes.shape({
    delete: PropTypes.string.isRequired,
    edit: PropTypes.string.isRequired,
    download: PropTypes.string.isRequired,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
  editLink: PropTypes.string.isRequired,
  pdfLink: PropTypes.string.isRequired,
  isDeleting: PropTypes.bool,
  templateId: PropTypes.string,
};

TileMenuComponent.defaultProps = {
  isDeleting: false,
};

export default TileMenuComponent;
